import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { fetchRisks } from '../risks/risksApi';
import type { RiskEntry } from '../risks/types';
import type { ObjectivePayload, ObjectiveStatus, QualityObjective } from './types';
import { OBJECTIVE_STATUS_LABEL } from './objectiveUtils';
import { usePlatformAccessLevel } from '../platformPermissions/usePlatformAccessLevel';

const STATUSES: ObjectiveStatus[] = ['borrador', 'activo', 'logrado', 'no_logrado', 'cancelado'];

interface ObjectiveFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: QualityObjective | null;
  onSubmit: (payload: ObjectivePayload) => Promise<void>;
}

function toDateInputValue(iso: string | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

export function ObjectiveFormModal({
  open,
  onOpenChange,
  initial,
  onSubmit,
}: ObjectiveFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [processArea, setProcessArea] = useState('');
  const [target, setTarget] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [status, setStatus] = useState<ObjectiveStatus>('borrador');
  const [relatedRiskId, setRelatedRiskId] = useState('');
  const [riskOptions, setRiskOptions] = useState<RiskEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { canView: canViewRisks } = usePlatformAccessLevel('risks.access');

  useEffect(() => {
    if (!open) return;
    const keepRiskId = initial?.relatedRiskId;
    void fetchRisks({ pageSize: 200 })
      .then((res) => {
        setRiskOptions(
          res.data.filter((risk) => risk.status !== 'cerrado' || risk.id === keepRiskId),
        );
      })
      .catch(() => setRiskOptions([]));
  }, [open, initial?.relatedRiskId]);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setProcessArea(initial?.processArea ?? '');
    setTarget(initial?.target ?? '');
    setPeriodStart(toDateInputValue(initial?.periodStart));
    setPeriodEnd(toDateInputValue(initial?.periodEnd));
    setStatus(initial?.status ?? 'borrador');
    setRelatedRiskId(initial?.relatedRiskId ?? '');
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || null,
        processArea: processArea.trim() || null,
        target: target.trim(),
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd).toISOString(),
        status,
        relatedRiskId: relatedRiskId || null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? 'Editar objetivo' : 'Nuevo objetivo'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">Título</label>
            <input
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={255}
              placeholder="Ej: Reducir no conformidades"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Meta cualitativa</label>
            <input
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              required
              maxLength={500}
              placeholder="Ej: Lograr cero reclamos críticos"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Inicio período</label>
              <input
                type="date"
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Fin período</label>
              <input
                type="date"
                className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Área de proceso</label>
            <input
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={processArea}
              onChange={(e) => setProcessArea(e.target.value)}
              maxLength={160}
              placeholder="Ej: Producción, Ventas…"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Riesgo relacionado</label>
            <select
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={relatedRiskId}
              onChange={(e) => setRelatedRiskId(e.target.value)}
            >
              <option value="">— Ninguno —</option>
              {riskOptions.map((risk) => (
                <option key={risk.id} value={risk.id}>
                  {risk.code} — {risk.title}
                </option>
              ))}
            </select>
            {!canViewRisks && (
              <p className="mt-1 text-xs text-muted-foreground">
                Necesitás permiso de visualización en Riesgos para asociar un riesgo.
              </p>
            )}
            {canViewRisks && riskOptions.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No hay riesgos disponibles para vincular (los cerrados no se listan).
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Estado</label>
            <select
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as ObjectiveStatus)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {OBJECTIVE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Descripción</label>
            <textarea
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm min-h-[80px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalle adicional del objetivo…"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={() => onOpenChange(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
